import { AnimationClip, Object3D } from "three";
import { GLTF } from "three/examples/jsm/loaders/GLTFLoader";
import { IFsFileType } from "@r3f-component-data-structure";
import { convertGltf } from "@r3f/utils/convertGltf";
import { gltfLoader } from "../utils";
import { FsBaseFile } from "./base";

export interface IGltfParsedData {
    scene: Object3D
    animations: AnimationClip[]
    meshToSkinnedMeshUuidDict: { [id: string]: string }
}

export interface IGltfData {
    parsedData: IGltfParsedData
    gltf: GLTF
}

export class FsModel3D extends FsBaseFile {
    type: IFsFileType.Model3d = IFsFileType.Model3d
    modelUrl: string
    thumbnailUrl: string

    private _modelArrayBuffer: ArrayBuffer | undefined
    private _parsedData: IGltfData | undefined

    constructor(fileId: string, name: string) {
        super(fileId, name)
        const modelUrl = new URL(this.baseUrl)
        modelUrl.pathname = `${modelUrl.pathname}/model.glb`
        this.modelUrl = modelUrl.toString()
        const thumbnailUrl = new URL(this.baseUrl)
        thumbnailUrl.pathname = `${thumbnailUrl.pathname}/frame0.png`
        this.thumbnailUrl = thumbnailUrl.toString()
    }

    async getUrl() {
        return this.modelUrl
    }

    getModelUrl() {
        return this.modelUrl
    }

    async getThumbnailUrl() {
        return this.thumbnailUrl
    }

    async getArrayBuffer() {
        if (typeof this._modelArrayBuffer === 'undefined') {
            const resp = await fetch(this.modelUrl)
            this._modelArrayBuffer = await resp.arrayBuffer()
        }
        return this._modelArrayBuffer
    }

    async getModelData(): Promise<IGltfData> {
        if (typeof this._parsedData !== 'undefined') return this._parsedData
        return new Promise(async resolve => {
            gltfLoader.load(await this.getUrl(), gltf => {
                this._parsedData = { parsedData: convertGltf(gltf), gltf }
                resolve(this._parsedData)
            })
        })
    }

    async load() {
        this.getArrayBuffer()
        return this
    }

    dispose(): void {
        super.dispose()
        // The raw GLB only — often the single biggest allocation in a project.
        this._modelArrayBuffer = undefined

        // `_parsedData` is deliberately NOT disposed. Either reason alone is disqualifying:
        //   1. useGLTFLoader hands out SkeletonUtils.clone()s that SHARE this scene's geometries and
        //      materials, so disposing them would corrupt any clone still mounted in the scene.
        //   2. The submodule's URL-keyed `modelCache` holds its own reference to the parsed model, so
        //      disposing here would not reclaim the memory anyway.
        // Model memory is reclaimed by `evictModelsNotIn()`, which is keyed on what the current content
        // doc still references and runs only after React has flushed.
    }
}
